
import { Component, Vue, Watch } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import * as XLSX from 'xlsx'
import {
  uploaMassivePatientsFile,
  getPatientsLoaded
} from '@/components/patients/patients.service'
import {
  beginPatientUpload,
  releasePatientUpload,
  trackPatientUpload
} from '@/patient-upload-session'
import {
  clearPatientUploadResultsDownload,
  downloadPatientUploadResults
} from '@/patient-upload-export'
import { PaginationVM } from '@/models'
import moment from 'moment'

const AppModule = namespace('app')

@Component
export default class UploadMassivePatientView extends Vue {
  @AppModule.Getter
  public _isUploadCompleted!: () => boolean

  @AppModule.Action('setUploadCompletedFlag')
  public setUploadCompletedFlag!: (flag: boolean) => Promise<void>
  public expandAllRows = false
  public fields = [
    { key: 'uploadIdentifier', label: 'Upload Identifier' },
    { key: 'npi', label: 'Npi' },
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'sex', label: 'Sex' },
    { key: 'dob', label: 'Dob', formatter: 'formatDate' },
    { key: 'insuranceName', label: 'Insurance Name' },
    { key: 'insurancePlan', label: 'Insurance Plan' },
    { key: 'phone', label: 'Phone' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'email1', label: 'Email1' },
    { key: 'street', label: 'Street' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'zip', label: 'Zip' },
    { key: 'insuranceContractNo', label: 'Insurance Contract No' },
    { key: 'uploadMessageStatus', label: 'Upload Message Status' },
    { key: 'uploadDate', label: 'Upload Date', formatter: 'formatDate' }
  ]
  public items = []
  public file: File | null = null
  public patientsLoaded = []
  public perPage = 10
  public pageOptions = [10, 20, 50, 100, 250, 500, 1000]
  public pagination = {} as PaginationVM
  public selectedSheet: string | null = null
  public sheetNames: string[] = []
  public previewRows: Record<string, unknown>[] = []
  public previewLoading = false
  public previewTruncated = false
  public previewLimit = 200
  public uploadIdToSync = ''
  public isLoadingAfterUpload = false
  public showDownloadBtn = false
  private previewWorkbook: XLSX.WorkBook | null = null

  @Watch('perPage')
  perPageChange() {
    this.getPatientsLoaded(this.pagination.currentPage)
  }

  @Watch('_isUploadCompleted')
  async onUploadCompleted(val: boolean) {
    if (val && this.uploadIdToSync) {
      this.isLoadingAfterUpload = true
      try {
        await this.getPatientsLoaded(1)
        // Auto-download is handled globally in App.vue; keep manual button ready.
      } finally {
        this.showDownloadBtn = true
      }
    }
  }

  @Watch('selectedSheet')
  onSelectedSheetChange() {
    this.buildPreviewFromWorkbook()
  }

  get _pagination() {
    return this.pagination
  }

  public async onPageChange(pageNumber: number) {
    await this.getPatientsLoaded(pageNumber)
  }

  public async loadFile(e: File | null) {
    this.file = e
    this.sheetNames = []
    this.selectedSheet = null
    this.previewRows = []
    this.previewTruncated = false
    this.previewWorkbook = null
    if (!e) return

    this.previewLoading = true
    try {
      // sheetRows limits parse work so 100k files don't freeze the preview.
      const workbook = XLSX.read(await e.arrayBuffer(), {
        type: 'array',
        sheetRows: this.previewLimit + 1,
        cellText: true,
        cellDates: true
      })
      this.previewWorkbook = workbook
      this.sheetNames = workbook.SheetNames || []
      this.selectedSheet = this.sheetNames[0] || null
      this.buildPreviewFromWorkbook()
    } catch (error) {
      console.error(error)
      this.$bvToast.toast('Could not build file preview.', {
        title: 'Error',
        variant: 'danger',
        solid: true,
        toaster: 'b-toaster-bottom-right',
        autoHideDelay: 4000
      })
    } finally {
      this.previewLoading = false
    }
  }

  private buildPreviewFromWorkbook() {
    if (!this.previewWorkbook || !this.selectedSheet) {
      this.previewRows = []
      this.previewTruncated = false
      return
    }
    const sheet = this.previewWorkbook.Sheets[this.selectedSheet]
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
      defval: '',
      raw: false
    })
    this.previewTruncated = rows.length >= this.previewLimit
    this.previewRows = rows.slice(0, this.previewLimit)
  }

  get patientUploadLoaded() {
    return this.$store.state.app.patientUploadLoaded
  }

  get patientUploadRemaining() {
    return this.$store.state.app.patientUploadRemaining
  }

  get patientUploadStaging() {
    return this.$store.state.app.patientUploadStaging
  }

  public async upload() {
    if (!this.file) return

    const formData = new FormData()
    formData.append('file', this.file)

    beginPatientUpload()
    try {
      const response = await uploaMassivePatientsFile(formData)

      if (response.success) {
        this.uploadIdToSync = response.batchId
        trackPatientUpload(
          response.batchId,
          response.total || 0,
          !!response.staging || !response.total
        )

        try {
          await this.getPatientsLoaded(1)
        } catch (error) {
          console.error(error)
        }

        this.$bvToast.toast(
          `Action submitted successfully. \n ${response.message}`,
          {
            title: 'Success',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 5000,
            appendToast: true
          }
        )
      } else {
        releasePatientUpload()
        this.$bvToast.toast(`An error has occur. \n ${response.message}`, {
          title: 'Warning',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 5000,
          appendToast: true
        })
      }
    } catch (error) {
      releasePatientUpload()
      console.error(error)
    }
  }
  public formatDate(value: string) {
    if (value) return moment(value).format('MM/DD/YYYY')
  }
  public async getPatientsLoaded(pageNumber) {
    if (!this.uploadIdToSync) return

    const response = await getPatientsLoaded(
      pageNumber,
      this.perPage,
      this.uploadIdToSync
    )
    this.patientsLoaded = response?.data || []
    this.pagination = JSON.parse(response.headers['x-pagination'])
  }

  public async syncData() {
    if (!this.uploadIdToSync) return

    try {
      // Allow manual re-download even if auto already ran for this batch.
      clearPatientUploadResultsDownload()
      const downloaded = await downloadPatientUploadResults(this.uploadIdToSync)
      await this.setUploadCompletedFlag(false)

      if (!downloaded) {
        this.$bvToast.toast('No data available to export', {
          title: 'Info',
          variant: 'info',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        })
        return
      }

      this.$bvToast.toast('Data exported successfully', {
        title: 'Success',
        variant: 'success',
        solid: true,
        toaster: 'b-toaster-bottom-right',
        autoHideDelay: 3000
      })
    } catch (error) {
      console.error('Error during sync:', error)
      this.$bvToast.toast('Error exporting data. Please try again.', {
        title: 'Error',
        variant: 'danger',
        solid: true,
        toaster: 'b-toaster-bottom-right',
        autoHideDelay: 3000
      })
    }
  }
}
